import { EditOutlined } from "@ant-design/icons";
import { Flex, Space, Tabs, Typography } from "antd";
import { VersionTag } from "../../../components/VersionTag.tsx";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BreadcrumbTrail } from "../../../components/BreadcrumbTrail.tsx";
import { SavingProvider } from "../../../hooks/useSaving.tsx";
import { SavingIndicator } from "../../../components/SavingIndicator.tsx";
import { useTrailLabel } from "../../../hooks/useTrailLabel.ts";
import { routes } from "../../../routes.ts";
import { useIngredient } from "../IngredientContext.tsx";
import { CancelEditButton } from "./CancelEditButton.tsx";
import { DiscardIngredientDraftButton } from "./DiscardIngredientDraftButton.tsx";
import { SaveIngredientButton } from "./PublishIngredientButton.tsx";
import { IngredientGeneralTab } from "./IngredientGeneralTab.tsx";
import { IngredientLabelIngredientsTab } from "./IngredientLabelIngredientsTab.tsx";
import { IngredientLabelTab } from "../IngredientLabelTab.tsx";
import { IngredientNutritionTab } from "./IngredientNutritionTab.tsx";
import { IngredientPhotosTab } from "./IngredientPhotosTab.tsx";
import { IngredientPriceTab } from "../IngredientPriceTab.tsx";
import { IngredientTab } from "../constants.ts";
import { IngredientUsedInRecipesTab } from "../IngredientUsedInRecipesTab.tsx";
import { title } from "../../../styles/shared.styles.ts";

const ingredientTabSchema = z.enum(IngredientTab).catch(IngredientTab.General);

export const IngredientDetailEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { ingredient } = useIngredient();
  useTrailLabel(t("common.editName", { name: ingredient.name }), EditOutlined);

  const activeTab = ingredientTabSchema.parse(location.hash.replace("#", ""));

  const setActiveTab = (tab: string) => {
    void navigate({ hash: tab }, { replace: true });
  };

  const handleDiscardCompleted = () => {
    void navigate({ pathname: routes.ingredientDetail.to(ingredient.id), hash: location.hash });
  };

  return (
    <SavingProvider>
      <Flex gap="medium" vertical>
        <Flex gap="small" vertical>
          <BreadcrumbTrail />
          <Space wrap>
            <Typography.Title className={title} level={3}>
              {ingredient.name}
            </Typography.Title>
            <VersionTag entity={ingredient} />
          </Space>
          <Space wrap>
            <SaveIngredientButton />
            <CancelEditButton />
            <DiscardIngredientDraftButton onCompleted={handleDiscardCompleted} />
            <SavingIndicator />
          </Space>
        </Flex>

        <Tabs
          activeKey={activeTab}
          destroyOnHidden
          items={[
            {
              key: IngredientTab.General,
              label: t("ingredients.tabGeneral"),
              children: <IngredientGeneralTab />,
            },
            {
              key: IngredientTab.LabelIngredients,
              label: t("ingredients.tabLabelIngredients", { count: ingredient.labelIngredients.length }),
              children: <IngredientLabelIngredientsTab />,
            },
            {
              key: IngredientTab.Nutrition,
              label: t("ingredients.tabNutrition"),
              children: <IngredientNutritionTab />,
            },
            {
              key: IngredientTab.Label,
              label: t("ingredients.tabLabel"),
              children: <IngredientLabelTab />,
            },
            {
              key: IngredientTab.Photos,
              label: t("ingredients.tabPhotos", { count: ingredient.photos.length }),
              children: <IngredientPhotosTab />,
            },
            {
              key: IngredientTab.Price,
              label: t("ingredients.tabPrice"),
              children: <IngredientPriceTab />,
            },
            {
              key: IngredientTab.UsedInRecipes,
              label: t("ingredients.tabUsedInRecipes", { count: ingredient.usedInRecipes.length }),
              children: <IngredientUsedInRecipesTab />,
            },
          ]}
          onChange={setActiveTab}
        />
      </Flex>
    </SavingProvider>
  );
};
